import logger from '@/lib/logger';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Trophy, Users as UsersIcon, Calendar, Check, Lightning, Fire, Barbell, Wind, Target } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHALLENGE_ICONS = { full_body: Barbell, abs: Fire, legs: Target, stress: Wind };

export default function Challenges() {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(null);

  const fetchChallenges = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/challenges`);
      setChallenges(data);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChallenges(); }, [fetchChallenges]);

  const joinChallenge = async (id) => {
    setJoining(id);
    try {
      await axios.post(`${API}/challenges/${id}/join`);
      setChallenges(prev => prev.map(c => c.id === id ? { ...c, joined: true, participants: (c.participants || 0) + 1, progress: 0 } : c));
    } catch (err) {
      logger.error(err);
    } finally {
      setJoining(null);
    }
  };

  const COLORS = ['#FF3B30', '#007AFF', '#34C759', '#FF9500'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const joined = challenges.filter(c => c.joined);
  const available = challenges.filter(c => !c.joined);

  return (
    <div data-testid="challenges-page">
      <div className="mb-8">
        <div className="label-tactical text-[#FF3B30] mb-1">Community</div>
        <h1 className="text-4xl font-black tracking-tighter">CHALLENGES</h1>
        <p className="text-[#A1A1AA] mt-2">Join structured programs and push yourself to the next level.</p>
      </div>

      {/* Active Challenges */}
      {joined.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold tracking-tight mb-4">YOUR ACTIVE CHALLENGES</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {joined.map((c, i) => {
              const color = COLORS[i % COLORS.length];
              const Icon = CHALLENGE_ICONS[c.image_type] || Trophy;
              return (
                <div key={c.id} className="tactical-card p-6" style={{ borderColor: `${color}30` }} data-testid={`active-challenge-${i}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 flex items-center justify-center" style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
                        <Icon weight="bold" className="w-6 h-6" style={{ color }} />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold tracking-tight">{c.name}</h3>
                        <span className="text-xs text-[#71717A]">{c.duration_days} day program</span>
                      </div>
                    </div>
                    <span className="flex items-center gap-1 text-xs font-bold text-[#34C759] bg-[#34C759]/10 px-2 py-1">
                      <Check weight="bold" className="w-3 h-3" /> ACTIVE
                    </span>
                  </div>
                  {/* Progress */}
                  <div className="mb-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-[#71717A]">Progress</span>
                      <span className="text-sm font-bold" style={{ color }}>{c.progress || 0}%</span>
                    </div>
                    <div className="h-2 bg-white/5 overflow-hidden">
                      <div className="h-full transition-all duration-500" style={{ width: `${c.progress || 0}%`, background: color }} />
                    </div>
                  </div>
                  <p className="text-xs text-[#71717A] mt-2">Complete daily workouts to increase your progress.</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Available Challenges */}
      {available.length > 0 && (
        <div>
          <h2 className="text-xl font-bold tracking-tight mb-4">
            {joined.length > 0 ? 'DISCOVER MORE' : 'AVAILABLE CHALLENGES'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {available.map((c, i) => {
              const color = COLORS[(i + joined.length) % COLORS.length];
              const Icon = CHALLENGE_ICONS[c.image_type] || Trophy;
              return (
                <div key={c.id} className="tactical-card p-6 hover:border-white/20 transition-all" data-testid={`challenge-card-${i}`}>
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center" style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
                      <Icon weight="bold" className="w-6 h-6" style={{ color }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold tracking-tight">{c.name}</h3>
                      <p className="text-sm text-[#A1A1AA] mt-1">{c.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-[#71717A] mb-4">
                    <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {c.duration_days} days</span>
                    <span className="flex items-center gap-1"><UsersIcon className="w-4 h-4" /> {c.participants || 0} joined</span>
                    <span className="uppercase text-xs font-bold" style={{ color }}>{c.level}</span>
                  </div>
                  <button onClick={() => joinChallenge(c.id)} disabled={joining === c.id}
                    className="btn-primary px-5 py-2.5 text-sm w-full flex items-center justify-center gap-2"
                    data-testid={`join-challenge-${i}`}>
                    {joining === c.id ? 'Joining...' : <><Lightning weight="bold" className="w-4 h-4" /> Join Challenge</>}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
